import toastr from 'toastr';

import { constants } from '@config';
import Store from '@store';

const moment = require('moment');

const DesignFacade = require('../design/design');

const RestrictionsFactory = require('./factory');
const MenuActionsConf = require('./menu-actions.json');

function RestrictionsFacade () {
  this.initialized = false;
  this.shownRestrictionMessages = {};
  this.handlers = {
    logo: require('./handlers/vector'),
    icon: require('./handlers/vector'),
    vector: require('./handlers/vector')
  };
}

function backgroundButton (button, elementType, backgroundPermissions) {
  if (['BACKGROUND_COLOR', 'BACKGROUND_COLOR_IMAGE', 'FILL_COLOR'].includes(button)) {
    return canChange('basic', 'color') && (!canChange('colors') && !canChange('colors-library'));
  }

  if (['SET_BACKGROUND', 'BACKGROUND_FILTER'].includes(button)) {
    const hasSpecificRestrictions = backgroundPermissions.some(permission => permission?.images);

    return canChange('basic', 'image') && !hasSpecificRestrictions;
  }

  return _.all(MenuActionsConf[elementType][button], function (action) {
    action = action.split('.');
    return canChange(action[0], action[1]);
  });

  function canChange (group, attribute) {
    return _.any(backgroundPermissions, function (p) {
      return p[group] && (!attribute || p[group][attribute]);
    });
  }
}

function getButtonType (button) {
  const alwaysAllowedActions = [
    'BLEED_CLOSE',
    'BLEED_SIZE',
    'DESELECT',
    'GROUP_UNGROUP',
    'HEADER_AND_FOOTER_CLOSE',
    'HEADER_AND_FOOTER_PAGE_SETTINGS',
    'LIBRARY_ADD_QUIET',
    'LIBRARY_ADD',
    'DETACH_SMART_ASSET'
  ];
  if (button.includes('COLOR')) return 'COLOR';
  if (alwaysAllowedActions.includes(button)) return 'ALWAYS_ALLOWED';
  return button;
}

function defaultButton (button, elementType, element, restrictions, defaultPermissions, isClippedImage) {
  const buttonType = getButtonType(button);

  if (button === 'HIGHLIGHT_COLOR' || button === 'CELL_COLOR') {
    return this.isActionAllowed(MenuActionsConf[elementType][button], element);
  }

  switch (buttonType) {
    case 'TEXT_EDITING':
      return this.isInlineTextAllowed(element);
    case 'VIDEO_SOURCE':
      return this.isVideoSourceReplacementAllowed(element);
    case 'SET_BACKGROUND':
      return this.isBackgroundReplacementAllowed(element);
    case 'GROUP_GROUP':
      return this.isGroupAllowed();
    case 'COLOR':
      return this.isColorAllowed(restrictions, defaultPermissions, element);
    case 'TEXT_TYPING':
      return this.isTextTypingAllowed(restrictions, defaultPermissions);
    case 'IMAGE_BG_REMOVAL':
      return this.isImageBgRemovalAllowed(restrictions, defaultPermissions);
    case 'ALWAYS_ALLOWED':
      return true;
    default:
      if (isClippedImage) return true;
      return !MenuActionsConf[elementType] || this.isActionAllowed(MenuActionsConf[elementType][button], element);
  }
}

function filterColorButtons (buttons, element) {
  const permissions = this.getAllElementPermissions(element.getAttribute('id'));
  if (!permissions?.length) return buttons;

  const permission = permissions[0];
  return buttons.filter((button) => {
    if (button === 'FILL_COLOR' || button === 'STROKE_COLOR' || button === 'TEXT_FILL_COLOR') {
      if (permission.colors || permission['colors-library']) {
        return false;
      }
    }
    return true;
  });
}

function filterBackgroundButtons (buttons, elementType) {
  const backgroundPermissions = this.getAllElementPermissions('background');
  if (!backgroundPermissions?.length) return buttons;

  return buttons.filter(button => backgroundButton(button, elementType, backgroundPermissions));
}

function filterRootButtons (buttons, elementType) {
  return buttons.filter((button) => {
    return this.isFunctionalityAllowed(MenuActionsConf[elementType][button]);
  });
}

function filterCellTextButtons (buttons, element) {
  const table = element.closest('.table');
  const tablePermissions = this.getAllElementPermissions(table.id);
  const tableRestrictions = tablePermissions.length && tablePermissions[0];

  return buttons.filter((button) => {
    const tableButtons = constants.restrictions.tableButtons;
    if (!tableButtons.includes(button)) return true;

    if (button.includes('COLOR') && tableRestrictions?.basic?.table_text_color === false) return false;

    const textStyleRestriction = tableRestrictions?.basic?.table_text_style;
    if (typeof textStyleRestriction === 'undefined') return true;

    return textStyleRestriction;
  });
}

function filterDefaultButtons (buttons, element, elementType) {
  const permissions = this.getAllElementPermissions(element.id);
  const isClippedImage = elementType === 'image' && element.classList.contains('layoutClipperImage');
  const defaultPermissions = this.getAllDefaultPermissions();
  const restrictions = permissions.length && permissions[0];

  return buttons.filter(button => defaultButton.call(this, button, elementType, element, restrictions, defaultPermissions, isClippedImage));
}

RestrictionsFacade.prototype.initialize = async function () {
  if (this.initializePromise) {
    this.initialized = true;
    return this.initializePromise;
  }

  _.bindAll(this,
    'canChange',
    'isFunctionalityAllowed',
    'isActionAllowed',
    'applyRestriction',
    'getElementRestrictions',
    'getAllElementPermissions',
    'elementHasRestrictions'
  );

  this.initialized = false;
  const factory = this.factory = new RestrictionsFactory();

  this.initializePromise = this.factory.initialize();
  this.initializePromise.then(() => {
    this.initialized = true;
    Object.values(this.handlers).forEach((handler) => handler.initialize(factory));

    this.factory.checkElementPermissions();
  });

  return this.initializePromise;
};

RestrictionsFacade.prototype.hasAddOnAndFeature = function (addOnName, feature) {
  return !this.initialized || this.factory.hasAddOnAndFeature(addOnName, feature);
};

RestrictionsFacade.prototype.isFunctionalityAllowed = function (functionality) {
  if (!this.factory || !this.factory.templatePermissions) {
    this.initialized = false;
  }
  if (!this.initialized) return true;

  if (Array.isArray(functionality) && functionality.includes('table_add')) {
    const isRedfin = this.factory.companyName && this.factory.companyName.toLowerCase() === 'redfin';
    if (isRedfin) return false;
  }

  return this.factory.templatePermissions.get(functionality);
};

RestrictionsFacade.prototype.filterMenuOptions = function (element, buttons) {
  if (!element) return;

  if (this.isForbiddenElement(element)) return ['REMOVE'];
  if (!this.initialized) return buttons;

  const elementType = this.getElementType(element);
  const isTableCell = element.parentElement?.classList?.contains('table-cell');

  buttons = filterColorButtons.call(this, buttons, element);
  if (element.tagName === 'a' && !element.classList.contains('video')) return ['UNLINK'];
  if (elementType === 'ignore-restrictions') return buttons;
  if (elementType === 'background') return filterBackgroundButtons.call(this, buttons, elementType);
  if (elementType === 'root') return filterRootButtons.call(this, buttons, elementType);
  if (elementType === 'text' && isTableCell) return filterCellTextButtons.call(this, buttons, element);

  return filterDefaultButtons.call(this, buttons, element, elementType);
};

RestrictionsFacade.prototype.isGroupAllowed = function () {
  return INKIVE.C.BOOK.book_type !== INKIVE.C.BOOK_TYPE.RESTRICTED_DESIGN;
};

RestrictionsFacade.prototype.isRestrictionAllowed = function (defaultPermissions, restriction, otherChecks, defaultPermission) {
  const restrictionIsUndefined = typeof restriction === 'undefined';
  const restrictionIsEmptyObject = typeof restriction === 'object' && Object.keys(restriction).length === 0;

  if (restrictionIsUndefined || restrictionIsEmptyObject) {
    if (Object.keys(defaultPermissions).length === 0) return true;

    return defaultPermission;
  }

  return restriction && otherChecks;
};

RestrictionsFacade.prototype.isTextTypingAllowed = function (restrictions, defaultPermissions) {
  const list = restrictions.dictionary?.list || restrictions.source?.list;
  const libraryFolder = restrictions.source?.library_folder;
  const textRestriction = calculateTextRestriction(restrictions?.basic?.text_change_content, list?.length || libraryFolder?.length);
  return this.isRestrictionAllowed(
    defaultPermissions,
    textRestriction,
    !list || !list.length || !libraryFolder?.length,
    defaultPermissions?.text_change_content
  );

  function calculateTextRestriction (canChangeContent, canSpecifyText) {
    if (typeof canChangeContent === 'undefined') {
      if (typeof canSpecifyText === 'undefined') return canSpecifyText;
      return !canSpecifyText;
    } else if (canChangeContent) {
      return !canSpecifyText;
    }

    return canChangeContent;
  }
};

RestrictionsFacade.prototype.isImageBgRemovalAllowed = function (restrictions, defaultPermissions) {
  return this.isRestrictionAllowed(
    defaultPermissions,
    restrictions?.basic?.image_content,
    !restrictions?.source,
    defaultPermissions?.image_content
  );
};

RestrictionsFacade.prototype.isColorAllowed = function (restrictions, defaultPermissions, element) {
  const elementType = this.getElementType(element);
  return this.isRestrictionAllowed(
    defaultPermissions,
    restrictions?.basic?.color,
    restrictions && !restrictions.colors && !restrictions['colors-library'],
    defaultPermissions[`${elementType}_colour`]
  );
};

RestrictionsFacade.prototype.applyRestriction = function (element, action, data) {
  if (!this.initialized) return data;
  const elementType = this.getElementType(element);
  const methodName = 'restrict' + action.charAt(0).toUpperCase() + action.slice(1);
  const restrictions = this.getElementRestrictions(element.getAttribute('id'), action);

  if (!this.handlers[elementType] || !this.handlers[elementType][methodName]) {
    return data;
  }

  return this.handlers[elementType][methodName](element, restrictions, data);
};

RestrictionsFacade.prototype.canChange = function (element, action) {
  const type = this.getElementType(element);
  if (type === 'group') {
    const mainElement = Array.from(element.childNodes).filter(node => node.tagName !== 'title')[0];
    action = mainElement ? this.getElementType(mainElement) + '_' + action : this.getElementType(element) + '_' + action;
  } else {
    action = this.getElementType(element) + '_' + action;
  }
  return this.isActionAllowed(action, element);
};

RestrictionsFacade.prototype.isVideoSourceReplacementAllowed = function (el) {
  return this.isActionAllowed('video_replace', el) &&
    _(this.getAllElementPermissions(el.getAttribute('id'))).all(function (r) {
      return (r.basic && _.size(r) === 1) || !_.size(r);
    });
};

RestrictionsFacade.prototype.isInlineTextAllowed = function (el) {
  return this.isActionAllowed('text_change_content', el) &&
    _(this.getAllElementPermissions(el.getAttribute('id'))).all(function (r) {
      return (r.basic && _.size(r) === 1) || !_.size(r);
    });
};

RestrictionsFacade.prototype.isBackgroundReplacementAllowed = function (el) {
  const permissions = INKIVE.RestrictionsFacade.getAllElementPermissions('background');

  if (!permissions.length || !permissions[0].basic) {
    return false;
  }

  const { basic, images } = permissions[0];

  return basic?.image && !images;
};

RestrictionsFacade.prototype.isReplaceRestrictedSelected = function (fallbackElement) {
  let isRestrictedElement = false;
  const selectedElements = INKIVE.Tool.getInstance().editor.canvas.getSelectedElems();
  let selectedElement = selectedElements.length === 1 && selectedElements[0];

  if (!selectedElement) {
    selectedElement = fallbackElement;
  }

  if (INKIVE.C.BOOK_TYPE.RESTRICTED_DESIGN === INKIVE.C.BOOK.book_type && selectedElement) {
    const element = INKIVE.DesignFacade.get(selectedElement.id);
    if (element) {
      const id = element.id;
      const restrictions = this.getElementRestrictions(id)[0];
      let restriction;

      switch (element.type) {
        case 'background':
          restriction = 'image';
          break;
        case 'image':
          restriction = 'image_content';
          break;
        case 'shape':
        case 'vector':
          restriction = 'vector_replace';
          break;
        case 'logo':
          restriction = 'logo_replace';
          break;
        case 'icon':
          restriction = 'icon_replace';
          break;
        case 'qrcode':
          restriction = 'qrcode_replace';
          break;
        case 'sticker':
          restriction = element.placeholder ? 'image_content' : 'icon_replace';
          break;
        case 'video':
          restriction = 'video_replace';
          break;
        default:
          restriction = null;
          break;
      }

      if (restrictions && restrictions[restriction] === undefined) {
        restrictions[restriction] = this.isFunctionalityAllowed(restriction);
      }

      isRestrictedElement = restrictions && !_.isUndefined(restrictions[restriction]) && id;

      if (_.isUndefined(isRestrictedElement) && restriction) {
        isRestrictedElement = id;
      }
    }
  }

  return isRestrictedElement;
};

RestrictionsFacade.prototype.isReplaceAllowed = function (id) {
  const permissions = this.getAllElementPermissions(id);
  let isAllowed = true;
  const element = INKIVE.DesignFacade.get(id);
  let content;
  let list;

  if (!element) {
    return;
  }

  switch (element.type) {
    case 'background':
      content = 'image';
      list = 'images';
      break;
    case 'image':
      content = 'image_content';
      list = 'sources';
      break;
    case 'logo':
      content = 'logo_replace';
      list = 'source';
      break;
    case 'icon':
      content = 'icon_replace';
      list = 'source';
      break;
    case 'qrcode':
      content = 'qrcode_replace';
      list = 'source';
      break;
    case 'sticker':
      content = element.placeholder ? 'image_content' : 'icon_replace';
      list = 'source';
      break;
    case 'video':
      content = 'video_replace';
      list = 'sources';
      break;
    default:
      content = null;
      list = null;
      break;
  }

  if (permissions.length) {
    const basic = permissions[0]?.basic;

    if (basic && basic[content] === undefined) {
      basic[content] = this.isFunctionalityAllowed(content);
    }

    isAllowed = basic?.[content] && !permissions[0]?.[list];
  } else {
    isAllowed = this.getAllDefaultPermissions()[content];
  }

  return isAllowed;
};

RestrictionsFacade.prototype.isActionAllowed = function (checks, element) {
  if (!this.initialized || !element) return true;

  checks = _.isArray(checks) ? checks : [checks];

  const elementRestrictions = this.getElementRestrictions(
    _.isString(element)
      ? element
      : (element.getAttribute('data-element-restriction-id') || element.getAttribute('id'))
  );

  return hasElementRestrictions.call(this, checks)
    ? checkElementPermissions(checks)
    : _.every(checks, function (check) {
      const permission = this.factory.templatePermissions.get(check);
      return _.isUndefined(permission) ? ['color'].includes(check) : permission;
    }, this);

  function hasElementRestrictions (checks) {
    return _.any(checks, function (check) {
      return _.any(elementRestrictions, function (er) {
        return typeof er[check] !== 'undefined';
      });
    });
  }

  function checkElementPermissions (checks) {
    return _.any(elementRestrictions, function (er) {
      return _.any(checks, function (check) {
        return typeof er[check] === 'undefined' || er[check];
      });
    });
  }
};

RestrictionsFacade.prototype.getElementType = function (element) {
  return this.getType(DesignFacade.getType(element));
};

RestrictionsFacade.prototype.getType = function (type) {
  if (type === 'sticker' || type === 'shape') return 'vector';
  return type;
};

RestrictionsFacade.prototype.getElementName = function (id) {
  if (!this.initialized || !id) return;
  const page = this.getPermissionsPage();
  const elementPermissions = this.factory.elementsPermissions.filter({
    element_id: id,
    page_number: page
  });

  const namedPermission = _.find(elementPermissions, function (item) {
    return !!item.get('name');
  });

  if (!namedPermission) {
    return;
  }

  return namedPermission.get('name');
};

RestrictionsFacade.prototype.getAllElementPermissions = function (id) {
  if (!this.initialized || !id) return [];
  const page = this.getPermissionsPage();
  return _.chain(this.factory.elementsPermissions.filter({
    element_id: id,
    page_number: page
  }))
    .map(function (model) {
      try {
        return JSON.parse(model.get('restrictions'));
      } catch (error) {
        return null;
      }
    })
    .filter(Boolean)
    .compact().value();
};

RestrictionsFacade.prototype.getAllDefaultPermissions = function () {
  if (!this.initialized || !this.factory.templatePermissions) return {};
  return this.factory.templatePermissions.toJSON();
};

RestrictionsFacade.prototype.isRestrictedDesign = function () {
  return INKIVE.C.BOOK.book_type === INKIVE.C.BOOK_TYPE.RESTRICTED_DESIGN;
};

RestrictionsFacade.prototype.isRestrictedProject = function () {
  if (INKIVE.C.BOOK.book_type === INKIVE.C.BOOK_TYPE.TEMPLATE) return true;
  if (!this.isRestrictedDesign()) return false;

  const state = Store.getState();

  return Array.isArray(state.permission.element) && state.permission.element.length > 0;
};

RestrictionsFacade.prototype.getElementRestriction = function (id, restriction, type = 'basic') {
  const defaultRestrictions = this.getAllDefaultPermissions();
  const restrictions = this.getElementRestrictions(id, type);
  return restrictions.length
    ? restrictions.reduce((previous, current) => previous && current[restriction], true)
    : defaultRestrictions[restriction];
};

RestrictionsFacade.prototype.getElementRestrictions = function (id, type) {
  if (!this.initialized) return [];
  type = type || 'basic';
  const page = this.getPermissionsPage();

  return _.chain(this.factory.elementsPermissions.filter({
    element_id: id,
    page_number: page
  }))
    .map(function (model) {
      try {
        return JSON.parse(model.get('restrictions'))[type];
      } catch (error) {
        return null;
      }
    })
    .filter(Boolean)
    .compact().value();
};

RestrictionsFacade.prototype.getPermissionsPage = function () {
  return parseInt(DesignFacade.get('template', 'restricted').base_page || INKIVE.Tool._current_page_number);
};

RestrictionsFacade.prototype.elementHasRestrictions = function (element) {
  if (this.shownRestrictionMessages[element.id]) {
    return true;
  }

  let message;
  const elementPermissions = this.getAllElementPermissions(element.id)[0];

  if (!elementPermissions) return false;
  if (elementPermissions.character_number) {
    if (elementPermissions.character_number.min || elementPermissions.character_number.max) {
      message = INKIVE.TranslateHelper.translate('restriction_info.text_character_number_range', {
        min: elementPermissions.character_number.min || 0,
        max: elementPermissions.character_number.max
      });
    }
  }

  if (elementPermissions.currencies) {
    if (elementPermissions.currencies.min) {
      message = INKIVE.TranslateHelper.translate('restriction_info.text_currency_range', {
        min: elementPermissions.currencies.min,
        max: elementPermissions.currencies.max,
        currency: elementPermissions.currencies.currency
      });
    } else if (elementPermissions.currencies.currency) {
      message = INKIVE.TranslateHelper.translate('restriction_info.text_currency_symbol', {
        currency: elementPermissions.currencies.currency
      });
    }
  }

  if (elementPermissions.date && elementPermissions.date.daterange) {
    message = INKIVE.TranslateHelper.translate('restriction_info.text_date', {
      start: formatDate('start'),
      end: formatDate('end')
    });
  }

  if (message) {
    toastr.info(message);
    this.shownRestrictionMessages[element.id] = message;
    return true;
  }

  function formatDate (range) {
    const format = elementPermissions.date.dateformat;
    let dateString = elementPermissions.date.daterange[range];

    if (format) {
      dateString = moment(dateString, 'MM/DD/YY').format(format);
    }

    return dateString;
  }
};

RestrictionsFacade.prototype.isForbiddenElement = function (element) {
  return ($(element).is('.non-selectable'));
};

RestrictionsFacade.prototype.constructor = RestrictionsFacade;
module.exports = new RestrictionsFacade();
